// src/pages/accounting/Accounting.jsx
// OFFICIAL ACCOUNTING — link-only page (/accounting), no sidebar entry.
// Visible only to admins AND when Settings → Features → Accounting is ON.
// Journal + Trial Balance + VAT (Lebanon TVA 11%) + Manual adjusting entries.
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import { exportStyledExcel, r2, MONEY_FMT, TOTAL_FILL } from '../../utils/exportExcel';
import { TrialBalanceFull, BalanceSheetTab, ProfitLossView, ChartTab, AuditTab, LockDateControl } from './AccountingPlus';

const GREEN = '#2e9f86';
const TABS = ['Journal', 'Trial Balance', 'Profit & Loss', 'Balance Sheet', 'VAT (TVA)', 'Manual Entries', 'Chart of Accounts', 'Audit Trail'];

const firstOfMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtD = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Accounting() {
  const [tab, setTab] = useState('Journal');
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(todayStr());

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/settings').then(r => r.data),
  });

  if (settingsLoading) return <div className="p-10 text-center text-gray-400">Loading…</div>;

  if (!settings?.features?.accounting) {
    return (
      <div className="max-w-md mx-auto mt-20 bg-white rounded-xl border border-gray-200 p-8 text-center">
        <div className="text-4xl mb-3">📒</div>
        <h2 className="font-bold text-gray-800 mb-2">Accounting is not enabled</h2>
        <p className="text-sm text-gray-500">Turn it on in Settings → Features → Accounting, then reload this page.</p>
      </div>
    );
  }

  const params = { date_from: dateFrom, date_to: dateTo };

  return (
    <div className="text-gray-800">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <h1 className="text-2xl font-bold text-gray-900">📒 Accounting</h1>
        <span className="text-xs text-gray-400">all amounts in USD · auto-generated from real data</span>
        <LockDateControl settings={settings} />
        <div className="flex items-center gap-2 ml-auto text-sm">
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === t ? '' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            style={tab === t ? { color: GREEN, borderColor: GREEN } : {}}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'Journal' && <JournalTab params={params} />}
      {tab === 'Trial Balance' && <TrialBalanceFull params={params} />}
      {tab === 'Profit & Loss' && <ProfitLossView params={params} />}
      {tab === 'Balance Sheet' && <BalanceSheetTab params={params} />}
      {tab === 'Chart of Accounts' && <ChartTab />}
      {tab === 'Audit Trail' && <AuditTab params={params} />}
      {tab === 'VAT (TVA)' && <VatTab params={params} />}
      {tab === 'Manual Entries' && <ManualTab params={params} />}
    </div>
  );
}

/* ── Journal ─────────────────────────────────────────────── */
function JournalTab({ params }) {
  const { data, isLoading } = useQuery({
    queryKey: ['acc-journal', params],
    queryFn: () => api.get('/api/accounting/journal', { params }).then(r => r.data),
  });
  if (isLoading) return <Spin />;
  const entries = data?.entries || [];

  const exportXls = () => exportStyledExcel({
    filename: `journal_${params.date_from}_${params.date_to}`,
    sheetName: 'Journal', title: 'General Journal (USD)',
    subtitle: `${fmtD(params.date_from)} — ${fmtD(params.date_to)} · ${entries.length} entries`,
    columns: [
      { header: 'Date', key: 'date', width: 13 },
      { header: 'Ref', key: 'ref', width: 12 },
      { header: 'Memo', key: 'memo', width: 34 },
      { header: 'Account', key: 'account', width: 32 },
      { header: 'Debit', key: 'debit', width: 14, numFmt: MONEY_FMT, align: 'right' },
      { header: 'Credit', key: 'credit', width: 14, numFmt: MONEY_FMT, align: 'right' },
    ],
    rows: [
      ...entries.flatMap(e => e.lines.map((l, i) => ({
        date: i === 0 ? fmtD(e.date) : '', ref: i === 0 ? e.ref : '',
        memo: i === 0 ? e.memo + (e.source === 'manual' ? ' [MANUAL]' : '') : '',
        account: l.account,
        debit: l.debit > 0 ? r2(l.debit) : '', credit: l.credit > 0 ? r2(l.credit) : '',
      }))),
      { memo: 'TOTAL', debit: r2(data.totalDebit), credit: r2(data.totalCredit), __bold: true, __fill: TOTAL_FILL },
    ],
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <span className="text-sm text-gray-500">{entries.length} entries · Debit {money(data?.totalDebit)} = Credit {money(data?.totalCredit)} {data?.totalDebit === data?.totalCredit ? '✅' : '⚠️'}</span>
        <button onClick={exportXls} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg">Export Excel</button>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 640 }}>
          <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-3 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Ref</th>
              <th className="text-left px-3 py-2.5">Memo</th>
              <th className="text-left px-3 py-2.5">Account</th>
              <th className="text-right px-3 py-2.5">Debit</th>
              <th className="text-right px-3 py-2.5">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.length === 0 && <tr><td colSpan={6} className="text-center text-gray-400 py-10">No entries in this period.</td></tr>}
            {entries.map((e, ei) => e.lines.map((l, li) => (
              <tr key={`${ei}-${li}`} className={li === 0 ? 'border-t-2 border-gray-200' : ''}>
                <td className="px-3 py-1.5 text-xs text-gray-500 whitespace-nowrap">{li === 0 ? fmtD(e.date) : ''}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-blue-600">{li === 0 ? e.ref : ''}</td>
                <td className="px-3 py-1.5 text-gray-600 text-xs">{li === 0 ? <>{e.memo}{e.source === 'manual' && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold">MANUAL</span>}</> : ''}</td>
                <td className={`px-3 py-1.5 ${l.credit > 0 ? 'pl-8 text-gray-500' : 'text-gray-800'}`}>{l.account}</td>
                <td className="px-3 py-1.5 text-right font-medium">{l.debit > 0 ? money(l.debit) : ''}</td>
                <td className="px-3 py-1.5 text-right font-medium text-gray-600">{l.credit > 0 ? money(l.credit) : ''}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Trial Balance ───────────────────────────────────────── */
/* ── VAT (Lebanon TVA 11%) ───────────────────────────────── */
function VatTab({ params }) {
  const { data, isLoading } = useQuery({
    queryKey: ['acc-vat', params],
    queryFn: () => api.get('/api/accounting/vat', { params }).then(r => r.data),
  });
  if (isLoading) return <Spin />;
  if (!data) return null;

  const exportXls = () => exportStyledExcel({
    filename: `vat_tva_${params.date_from}_${params.date_to}`,
    sheetName: 'TVA', title: `VAT / TVA Report (${data.rate}%) — USD`,
    subtitle: `${fmtD(params.date_from)} — ${fmtD(params.date_to)}`,
    columns: [
      { header: 'Item', key: 'item', width: 40 },
      { header: 'USD', key: 'usd', width: 16, numFmt: MONEY_FMT, align: 'right' },
    ],
    rows: [
      { item: 'SALES (output)', __bold: true, __fill: TOTAL_FILL },
      { item: 'Gross sales (net of returns)', usd: data.sales.gross },
      { item: 'Net sales (excl. VAT)', usd: data.sales.net },
      { item: 'VAT collected', usd: data.sales.vat_collected, __bold: true },
      { item: 'PURCHASES (input)', __bold: true, __fill: TOTAL_FILL },
      { item: 'Gross purchases (net of returns)', usd: data.purchases.gross },
      { item: 'Net purchases (excl. VAT)', usd: data.purchases.net },
      { item: 'VAT deductible', usd: data.purchases.vat_deductible, __bold: true },
      { item: 'NET VAT DUE (to declare)', usd: data.net_vat_due, __bold: true, __fill: TOTAL_FILL },
    ],
  });

  const Card = ({ label, value, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className={`text-xl md:text-2xl font-bold ${color || 'text-gray-800'}`}>{money(value)}</p>
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <span className="text-sm text-gray-500">Lebanon TVA {data.rate}% · declared quarterly</span>
        <button onClick={exportXls} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg">Export Excel</button>
      </div>
      {data.note && <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">{data.note}</div>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <Card label="VAT collected on sales" value={data.sales.vat_collected} color="text-blue-600" />
        <Card label="VAT deductible on purchases" value={data.purchases.vat_deductible} color="text-emerald-600" />
        <Card label="NET VAT DUE" value={data.net_vat_due} color={data.net_vat_due > 0 ? 'text-red-600' : 'text-emerald-600'} />
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm space-y-2">
        <div className="flex justify-between"><span className="text-gray-500">Sales (net of returns, incl. VAT)</span><b>{money(data.sales.gross)}</b></div>
        <div className="flex justify-between"><span className="text-gray-500">Sales excl. VAT</span><b>{money(data.sales.net)}</b></div>
        <div className="flex justify-between border-t pt-2"><span className="text-gray-500">Purchases (net of returns, incl. VAT)</span><b>{money(data.purchases.gross)}</b></div>
        <div className="flex justify-between"><span className="text-gray-500">Purchases excl. VAT</span><b>{money(data.purchases.net)}</b></div>
      </div>
    </div>
  );
}

/* ── Manual adjusting entries ────────────────────────────── */
function ManualTab({ params }) {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayStr());
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState([{ account: '', debit: '', credit: '' }, { account: '', debit: '', credit: '' }]);
  const [error, setError] = useState('');

  const { data: journal } = useQuery({
    queryKey: ['acc-journal', params],
    queryFn: () => api.get('/api/accounting/journal', { params }).then(r => r.data),
  });
  const accounts = journal?.accounts || [];

  const { data: list = [] } = useQuery({
    queryKey: ['acc-manual', params],
    queryFn: () => api.get('/api/accounting/entries', { params }).then(r => r.data),
  });

  const addMutation = useMutation({
    mutationFn: (body) => api.post('/api/accounting/entries', body),
    onSuccess: () => {
      queryClient.invalidateQueries(['acc-manual']); queryClient.invalidateQueries(['acc-journal']); queryClient.invalidateQueries(['acc-tb']);
      setMemo(''); setLines([{ account: '', debit: '', credit: '' }, { account: '', debit: '', credit: '' }]); setError('');
    },
    onError: (err) => setError(err.response?.data?.error || 'Error'),
  });
  const delMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/accounting/entries/${id}`),
    onSuccess: () => { queryClient.invalidateQueries(['acc-manual']); queryClient.invalidateQueries(['acc-journal']); queryClient.invalidateQueries(['acc-tb']); },
  });

  const setLine = (i, k, v) => setLines(ls => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const dr = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const cr = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = r2(dr) === r2(cr) && dr > 0;

  const submit = () => {
    setError('');
    addMutation.mutate({ date, memo, lines: lines.filter(l => l.account).map(l => ({ account: l.account, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })) });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* new entry */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-bold text-gray-800 mb-3">➕ New Manual Entry <span className="text-xs font-normal text-gray-400">(depreciation, salaries, opening balances…)</span></h3>
        <div className="flex gap-2 mb-3">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          <input value={memo} onChange={e => setMemo(e.target.value)} placeholder="Memo / البيان" className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        {lines.map((l, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <select value={l.account} onChange={e => setLine(i, 'account', e.target.value)} className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-2 text-sm">
              <option value="">— account —</option>
              {accounts.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <input type="number" min="0" step="0.01" placeholder="Debit" value={l.debit} onChange={e => setLine(i, 'debit', e.target.value)} className="w-24 border border-gray-300 rounded-lg px-2 py-2 text-sm text-right" />
            <input type="number" min="0" step="0.01" placeholder="Credit" value={l.credit} onChange={e => setLine(i, 'credit', e.target.value)} className="w-24 border border-gray-300 rounded-lg px-2 py-2 text-sm text-right" />
          </div>
        ))}
        <div className="flex items-center gap-3 mt-2">
          <button onClick={() => setLines(ls => [...ls, { account: '', debit: '', credit: '' }])} className="text-sm text-blue-600 font-medium">+ line</button>
          <span className={`text-xs font-bold ml-auto ${balanced ? 'text-emerald-600' : 'text-red-500'}`}>Dr {money(dr)} / Cr {money(cr)} {balanced ? '✅' : ''}</span>
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
        <button onClick={submit} disabled={!balanced || addMutation.isPending}
          className="mt-3 w-full py-2.5 rounded-lg text-white font-bold text-sm disabled:opacity-40" style={{ background: GREEN }}>
          {addMutation.isPending ? 'Saving…' : 'Save Entry'}
        </button>
      </div>

      {/* list */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="font-bold text-gray-800 mb-3">Manual entries in period ({list.length})</h3>
        {list.length === 0 && <div className="text-gray-400 text-sm py-6 text-center">None yet.</div>}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {list.map(m => (
            <div key={m._id} className="border border-gray-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between items-center mb-1">
                <b className="text-gray-800">{m.memo || '(no memo)'}</b>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{fmtD(m.date)} · {m.created_by}</span>
                  <button onClick={() => { if (window.confirm('Delete this manual entry?')) delMutation.mutate(m._id); }} className="text-red-400 font-bold">×</button>
                </div>
              </div>
              {m.lines.map((l, i) => (
                <div key={i} className="flex justify-between text-xs text-gray-600">
                  <span className={l.credit > 0 ? 'pl-4' : ''}>{l.account}</span>
                  <span>{l.debit > 0 ? `Dr ${money(l.debit)}` : `Cr ${money(l.credit)}`}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Spin() {
  return <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;
}
